import { MdKeyboardArrowDown, MdRoundSearch } from "@teamworksdev/icons";
import {
  Avatar,
  Badge,
  Button,
  Checkbox,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@teamworksdev/react";
import { type ComponentProps, useMemo, useState } from "react";
import { useAllOrgsSimpleQuery } from "~/data/orgs";
import type { OrgItem } from "~/data/orgs/types";
import { usePartialMatchCountsQuery } from "~/modules/PartialMatches/queries";
import { sortingFns } from "~/utils/sorting";
import BasePicker from "./BasePicker";

type OrgPickerProps = {
  selectedOrgId?: string;
  onChange: (orgId: string) => void;
  orgOptions?: ComponentProps<typeof BasePicker>["options"];
};

export const OrgPicker = ({ selectedOrgId, onChange, orgOptions }: OrgPickerProps) => {
  const { data: defaultOrgOptions } = useAllOrgsSimpleQuery({
    select: (data) =>
      data.map((org) => ({
        label: org.name ?? "Unknown",
        value: org.id,
      })),
    enabled: !orgOptions,
  });

  return (
    <BasePicker
      noOptionsText="No organizations found."
      onChange={onChange}
      options={orgOptions ?? defaultOrgOptions ?? []}
      placeholder="Select organization..."
      searchPlaceholder="Search organizations..."
      selectedValue={selectedOrgId}
    />
  );
};

const MAX_SEARCH_RESULTS = 100;

type OrgPicker2Props = {
  selectedOrgId?: string;
  onChange: (orgId: string) => void;
};

const sortOrgs = (orgs: OrgItem[]) => orgs.sort(sortingFns.alphanumeric("name"));

export const OrgPicker2 = ({ selectedOrgId, onChange }: OrgPicker2Props) => {
  const [open, setOpen] = useState(false);
  const [showOnlyPartialMatches, setShowOnlyPartialMatches] = useState(false);
  const [search, setSearch] = useState("");

  const { data: orgs } = useAllOrgsSimpleQuery({ select: sortOrgs });

  const { data: partialMatchCounts } = usePartialMatchCountsQuery({
    params: { by: "org" },
  });

  const searchResults = useMemo(() => {
    const orgsForSearch = showOnlyPartialMatches
      ? orgs?.filter((org) => (partialMatchCounts?.[org.id] ?? 0) > 0)
      : orgs;

    const filteredOrgs = search
      ? orgsForSearch?.filter((org) => org.name.toLowerCase().includes(search.toLowerCase()))
      : orgsForSearch;

    return showOnlyPartialMatches ? filteredOrgs : filteredOrgs?.slice(0, MAX_SEARCH_RESULTS);
  }, [orgs, partialMatchCounts, showOnlyPartialMatches, search]);

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button
          aria-expanded={open}
          variant="ghost"
          className="w-full flex justify-between border border-light-300 hover:bg-white font-normal [&>svg]:data-[state=open]:rotate-180"
          disableAnimation
        >
          <span className="truncate">Select organization</span>
          <MdKeyboardArrowDown className="transition-transform shrink-0" size={20} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[350px] z-50 bg-white" align="start">
        <div className="p-3 pb-0 flex flex-col gap-2">
          <Label className="text-xs font-semibold text-black/mid-emphasis">Show organizations with</Label>
          <div className="flex items-center gap-2">
            {/** biome-ignore lint/correctness/useUniqueElementIds: TODO: Use a unique id with useId() */}
            <Checkbox
              id="partial-matches"
              isChecked={showOnlyPartialMatches}
              onCheckedChange={(checked) => setShowOnlyPartialMatches(!!checked)}
            />
            <Label htmlFor="partial-matches" className="text-sm font-normal cursor-pointer">
              Partial matches
            </Label>
          </div>
        </div>
        <Command className="p-3" shouldFilter={false}>
          <CommandInput
            autoFocus
            className="p-0 h-9"
            inputWrapperClassName="border rounded-md mb-1"
            leftIcon={<MdRoundSearch size={20} className="mr-2 text-black/low-emphasis" />}
            placeholder="Search organizations"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="mt-2">
            <CommandEmpty>No organizations found.</CommandEmpty>
            {searchResults?.map((org) => {
              const searchKey = org.name;

              return (
                <CommandItem
                  key={org.id}
                  onSelect={() => {
                    if (org.id !== selectedOrgId) {
                      setSearch("");
                      setShowOnlyPartialMatches(false);
                      onChange(org.id);
                    }
                    setOpen(false);
                  }}
                  value={`${searchKey} --- ${org.id}`}
                >
                  {/** biome-ignore lint/a11y/useFocusableInteractive: TODO: Can we use a better element here? */}
                  <div
                    key={org.id}
                    className="flex items-center gap-2 cursor-pointer w-full"
                    role="option"
                    aria-selected={org.id === selectedOrgId}
                  >
                    <Avatar className="shrink-0" src={org.navbarImageUrl} name={org.name} size={24} />
                    <span className="truncate text-sm">{org.name}</span>
                    <Badge className="ml-auto" value={partialMatchCounts?.[org.id] ?? 0} colorScheme="count" />
                  </div>
                </CommandItem>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
